import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { generateXeroState } from '../../utils/xeroAuthState';
import { XeroAuthLayout } from './XeroAuthLayout';
import { XeroTenantAccountField } from './XeroTenantAccountField';
import { XERO_AUTH_STEP_INDEX } from './xeroAuthSteps';
import { XERO_COLORS, XERO_SPACING } from './xeroAuthStyles';
import { Button } from '../../components';
import { Alert, Stack, CircularProgress, Typography } from '@mui/material';

export const XeroUserAuthRedirect = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authKey, setAuthKey] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [tenantBankAccounts, setTenantBankAccounts] = useState({});
  const [tenantAccountOptions, setTenantAccountOptions] = useState({});
  const [success, setSuccess] = useState(false);

  if (window.location.hostname !== 'xero-auth.oligo.com.au' && window.location.hostname !== 'localhost') {
    window.location = '/' + window.location.search;
  }

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');

    if (!code) {
      setError('Missing authorization code in URL.');
      setLoading(false);
      return;
    }

    const expectedState = generateXeroState();
    if (state !== expectedState) {
      setError('Invalid or expired authorization request. Please restart the Xero connection from Oligo.');
      setLoading(false);
      return;
    }

    exchangeCode(code);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const exchangeCode = async (code) => {
    try {
      const res = await fetch('/api/xero/oauth/exchange', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to exchange code');
      }

      setAuthKey(data.authKey);
      fetchTenants(data.authKey);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Error occurred during authorization.');
      setLoading(false);
    }
  };

  const fetchTenants = async (key) => {
    try {
      const res = await fetch(`/api/xero/tenants?authKey=${encodeURIComponent(key)}`);
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to fetch tenants');
      }

      const fetchedTenants = data.tenants || [];
      const existingSelections = data.tenantBankAccounts || {};
      setTenants(fetchedTenants);
      setTenantBankAccounts(existingSelections);

      await Promise.all(
        fetchedTenants.map((tenant) => fetchBankAccounts(key, tenant.tenantId, existingSelections[tenant.tenantId]))
      );
    } catch (err) {
      console.error(err);
      setError(err.message || 'Error fetching tenants.');
    } finally {
      setLoading(false);
    }
  };

  const fetchBankAccounts = async (key, tenantId, existingAccountId) => {
    try {
      const res = await fetch(`/api/xero/bank-accounts?authKey=${encodeURIComponent(key)}&tenantId=${encodeURIComponent(tenantId)}`);
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to fetch bank accounts');
      }

      const accounts = data.bankAccounts || [];
      setTenantAccountOptions((prev) => ({ ...prev, [tenantId]: accounts }));

      if (!tenantBankAccounts[tenantId]) {
        const defaultAccountId = existingAccountId && accounts.some((a) => a.accountId === existingAccountId)
          ? existingAccountId
          : accounts[0]?.accountId || '';
        setTenantBankAccounts((prev) => ({ ...prev, [tenantId]: defaultAccountId }));
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Error fetching bank accounts.');
    }
  };

  const handleAccountChange = (tenantId, accountId) => {
    setTenantBankAccounts((prev) => ({ ...prev, [tenantId]: accountId }));
  };

  const handleClearAccount = (tenantId) => {
    setTenantBankAccounts((prev) => ({ ...prev, [tenantId]: '' }));
  };

  const handleSubmit = async (e) => {
    e && e.preventDefault();
    if (!authKey) return;

    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/xero/tenant/select', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ authKey, tenantBankAccounts }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to save bank account selections');
      }
      setSuccess(true);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Error saving bank account selections.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <XeroAuthLayout activeIndex={XERO_AUTH_STEP_INDEX.accounts}>
        <Stack alignItems="center" justifyContent="center" height="200px" gap={XERO_SPACING.field}>
          <CircularProgress />
          <Typography color="textSecondary">
            Loading... please wait.
          </Typography>
        </Stack>
      </XeroAuthLayout>
    );
  }

  if (error) {
    return (
      <XeroAuthLayout activeIndex={XERO_AUTH_STEP_INDEX.accounts} hasError>
        <Stack gap={XERO_SPACING.group}>
          <Alert severity="error">
            <strong>Error connecting to Xero:</strong> {error}
          </Alert>
          <Button $themeColor="#000000" block onClick={() => navigate(-1)}>
            Go Back
          </Button>
        </Stack>
      </XeroAuthLayout>
    );
  }

  if (success) {
    return (
      <XeroAuthLayout activeIndex={XERO_AUTH_STEP_INDEX.allComplete}>
        <Stack gap={XERO_SPACING.group}>
          <Alert severity="success">
            Your Xero bank account selections have been successfully saved. Please close this window.
          </Alert>
        </Stack>
      </XeroAuthLayout>
    );
  }

  return (
    <XeroAuthLayout activeIndex={XERO_AUTH_STEP_INDEX.accounts}>
      <Stack gap={XERO_SPACING.group}>
        <Stack gap={XERO_SPACING.text}>
          <Typography variant="h6" fontWeight="600" color={XERO_COLORS.heading}>
            Select Xero bank accounts
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Choose the Xero bank account you want to use for payments for each organisation.
          </Typography>
        </Stack>

        <Stack gap={XERO_SPACING.field}>
          {tenants.map((tenant) => (
            <XeroTenantAccountField
              key={tenant.tenantId}
              tenant={tenant}
              accounts={tenantAccountOptions[tenant.tenantId] || []}
              selectedAccountId={tenantBankAccounts[tenant.tenantId] || ''}
              onChange={(accountId) => handleAccountChange(tenant.tenantId, accountId)}
              onClear={() => handleClearAccount(tenant.tenantId)}
            />
          ))}
        </Stack>

        <Button $themeColor="#000000" block onClick={(e) => handleSubmit(e)}>
          Save Bank Accounts
        </Button>
      </Stack>
    </XeroAuthLayout>
  );
}

export default XeroUserAuthRedirect;
